/**
 * Birth chart wheel — SVG port of birth_chart/renderer.py (natal only).
 *
 * API:
 *   renderBirthChartWheel(container, chartData, { iconsBaseUrl })
 *
 * chartData matches BirthChartCalculator.calculate_birth_chart() output:
 *   planet_positions[*].longitude, house_cusps[], aspects[], angles{}
 *
 * Matplotlib is y-up; SVG is y-down. Geometry is computed in matplotlib space,
 * then mapped with ySvg = canvasSize - y for paths/circles. Icons stay upright.
 */
(function (global) {
  "use strict";

  const NS = "http://www.w3.org/2000/svg";
  const XLINK = "http://www.w3.org/1999/xlink";

  // Mirror BirthChartRenderer.__init__
  const canvasSize = 1200;
  const center = { x: 600, y: 600 };
  const R = 560;

  const signRingOuter = R * 0.97;
  const oldInner = R * 0.7;
  const oldThick = signRingOuter - oldInner;
  const newThick = oldThick * 0.75;
  const signRingInner = signRingOuter - newThick;
  const signIconRadius = (signRingOuter + signRingInner) / 2.0;

  const houseRingOuter = R * 0.28;
  const houseRingInner = R * 0.18;
  const houseIconRadius = (houseRingInner + houseRingOuter) / 2.0;

  const positionRadius = signRingInner - R * 0.03;
  const planetTickLen = R * 0.02;
  const iconGap = R * 0.07;
  const nodeGap = R * 0.07;
  const nodeRadiusPx = 6;

  let planetIconRadius = positionRadius - (planetTickLen + iconGap);
  let nodeRadius = planetIconRadius - nodeGap;
  nodeRadius = Math.max(nodeRadius, houseRingOuter + R * 0.04);

  const canvasRatio = canvasSize / 1500.0;
  const dpiCompensation = 100.0 / 72.0;
  const signIconSize = Math.floor(48 * canvasRatio * dpiCompensation * 1.7); // ~90
  const houseIconSize = Math.floor(32 * canvasRatio * dpiCompensation); // 35
  const planetIconSize = Math.floor(48 * canvasRatio * dpiCompensation * 1.6); // ~85
  // AC/MC use HEAVY (thickest); a bit smaller than planets
  const angleIconSize = Math.floor(48 * canvasRatio * dpiCompensation * 0.95); // ~50

  const SIGNS = [
    "ARIES",
    "TAURUS",
    "GEMINI",
    "CANCER",
    "LEO",
    "VIRGO",
    "LIBRA",
    "SCORPIO",
    "SAGITTARIUS",
    "CAPRICORN",
    "AQUARIUS",
    "PISCES",
  ];

  // All glyphs from icons/normalized/*.svg (no PNG).
  // Planets: PLANETS/*_BOLD.svg
  // AC/MC: PLANETS/AC_S90.svg, MC_S90.svg
  // Signs: ASTRO SIGNS/*_HEAVY.svg
  // Houses: HOUSES/HOUSE_01.svg … HOUSE_12.svg
  const PLANET_WEIGHT = "BOLD";
  const SIGN_WEIGHT = "HEAVY";
  const ICON_FILES = {
    Sun: `PLANETS/SUN_${PLANET_WEIGHT}.svg`,
    Moon: `PLANETS/MOON_${PLANET_WEIGHT}.svg`,
    Mercury: `PLANETS/MERCURY_${PLANET_WEIGHT}.svg`,
    Venus: `PLANETS/VENUS_${PLANET_WEIGHT}.svg`,
    Mars: `PLANETS/MARS_${PLANET_WEIGHT}.svg`,
    Jupiter: `PLANETS/JUPITER_${PLANET_WEIGHT}.svg`,
    Saturn: `PLANETS/SATURN_${PLANET_WEIGHT}.svg`,
    Uranus: `PLANETS/URANUS_${PLANET_WEIGHT}.svg`,
    Neptune: `PLANETS/NEPTUNE_${PLANET_WEIGHT}.svg`,
    Pluto: `PLANETS/PLUTO_${PLANET_WEIGHT}.svg`,
    "North Node": `PLANETS/NORTH_NODE_${PLANET_WEIGHT}.svg`,
    Ascendant: "PLANETS/AC_S90.svg",
    MC: "PLANETS/MC_S90.svg",
    ARIES: `ASTRO SIGNS/ARIES_${SIGN_WEIGHT}.svg`,
    TAURUS: `ASTRO SIGNS/TAURUS_${SIGN_WEIGHT}.svg`,
    GEMINI: `ASTRO SIGNS/GEMINI_${SIGN_WEIGHT}.svg`,
    CANCER: `ASTRO SIGNS/CANCER_${SIGN_WEIGHT}.svg`,
    LEO: `ASTRO SIGNS/LEO_${SIGN_WEIGHT}.svg`,
    VIRGO: `ASTRO SIGNS/VIRGO_${SIGN_WEIGHT}.svg`,
    LIBRA: `ASTRO SIGNS/LIBRA_${SIGN_WEIGHT}.svg`,
    SCORPIO: `ASTRO SIGNS/SCORPIO_${SIGN_WEIGHT}.svg`,
    SAGITTARIUS: `ASTRO SIGNS/SAGITTARIUS_${SIGN_WEIGHT}.svg`,
    CAPRICORN: `ASTRO SIGNS/CAPRICORN_${SIGN_WEIGHT}.svg`,
    AQUARIUS: `ASTRO SIGNS/AQUARIUS_${SIGN_WEIGHT}.svg`,
    PISCES: `ASTRO SIGNS/PISCES_${SIGN_WEIGHT}.svg`,
    1: "HOUSES/HOUSE_01.svg",
    2: "HOUSES/HOUSE_02.svg",
    3: "HOUSES/HOUSE_03.svg",
    4: "HOUSES/HOUSE_04.svg",
    5: "HOUSES/HOUSE_05.svg",
    6: "HOUSES/HOUSE_06.svg",
    7: "HOUSES/HOUSE_07.svg",
    8: "HOUSES/HOUSE_08.svg",
    9: "HOUSES/HOUSE_09.svg",
    10: "HOUSES/HOUSE_10.svg",
    11: "HOUSES/HOUSE_11.svg",
    12: "HOUSES/HOUSE_12.svg",
  };

  const ASPECT_STYLES = {
    conjunction: { linewidth: 4.2, color: "#6833c4" },
    sextile: { linewidth: 4.2, color: "#2563EB" },
    square: { linewidth: 4.2, color: "#B91C1C" },
    trine: { linewidth: 4.2, color: "#166534" },
    opposition: { linewidth: 4.2, color: "#ca8a04" },
  };

  /**
   * Circumferential spacing weights from glyph WIDTH × display size.
   * Measured vs icons/normalized PLANETS (BOLD / AC_S90 / MC_S90).
   * 1.0 = previous uniform spacing (largest: North Node).
   * Floor 0.84 — mild packing only for the smallest glyphs.
   */
  const ICON_SPACING_WEIGHT = {
    "North Node": 1.0,
    Uranus: 0.962,
    MC: 0.957,
    Mars: 0.948,
    Ascendant: 0.944,
    Sun: 0.937,
    Jupiter: 0.908,
    Pluto: 0.876,
    Neptune: 0.872,
    Moon: 0.852,
    Venus: 0.847,
    Mercury: 0.84,
    Saturn: 0.84,
  };

  function spacingWeight(name) {
    const w = ICON_SPACING_WEIGHT[name];
    return w == null ? 1.0 : w;
  }

  function el(tag, attrs, parent) {
    const node = document.createElementNS(NS, tag);
    if (attrs) {
      for (const [k, v] of Object.entries(attrs)) {
        if (v === undefined || v === null) continue;
        if (k === "href") {
          node.setAttributeNS(XLINK, "href", v);
          node.setAttribute("href", v);
        } else {
          node.setAttribute(k, String(v));
        }
      }
    }
    if (parent) parent.appendChild(node);
    return node;
  }

  function toSvg(x, y) {
    return { x, y: canvasSize - y };
  }

  function mod(n, m) {
    return ((n % m) + m) % m;
  }

  function longitudeToAngle(longitude, ascendantLongitude) {
    if (ascendantLongitude != null) {
      const thetaDeg = mod(180 + (longitude - ascendantLongitude), 360);
      return (thetaDeg * Math.PI) / 180;
    }
    return (mod(180 + longitude, 360) * Math.PI) / 180;
  }

  function angleToPosition(angle, radius) {
    return {
      x: radius * Math.cos(angle),
      y: radius * Math.sin(angle),
    };
  }

  function planetGeo(longitude, ascendantLongitude) {
    const angle = longitudeToAngle(longitude, ascendantLongitude);
    const on = angleToPosition(angle, positionRadius);
    const tick = angleToPosition(angle, positionRadius - planetTickLen);
    const icon = angleToPosition(angle, planetIconRadius);
    const node = angleToPosition(angle, nodeRadius);
    return {
      on: { x: on.x + center.x, y: on.y + center.y },
      tick: { x: tick.x + center.x, y: tick.y + center.y },
      icon: { x: icon.x + center.x, y: icon.y + center.y },
      node: { x: node.x + center.x, y: node.y + center.y },
      angle,
    };
  }

  function argsort(arr) {
    return arr
      .map((v, i) => [v, i])
      .sort((a, b) => a[0] - b[0])
      .map((p) => p[1]);
  }

  function unwrap(angles) {
    const out = angles.slice();
    for (let i = 1; i < out.length; i++) {
      let d = out[i] - out[i - 1];
      while (d > Math.PI) {
        out[i] -= 2 * Math.PI;
        d = out[i] - out[i - 1];
      }
      while (d < -Math.PI) {
        out[i] += 2 * Math.PI;
        d = out[i] - out[i - 1];
      }
    }
    return out;
  }

  /** Pool-Adjacent-Violators — mirror renderer._compute_icon_angle_offsets.pav */
  function pav(y) {
    const n = y.length;
    const v = y.slice();
    const w = Array(n).fill(1.0);
    const i0 = Array.from({ length: n }, (_, i) => i);
    const i1 = Array.from({ length: n }, (_, i) => i);
    let k = 0;
    while (k < v.length - 1) {
      if (v[k] <= v[k + 1]) {
        k += 1;
        continue;
      }
      const nv = (w[k] * v[k] + w[k + 1] * v[k + 1]) / (w[k] + w[k + 1]);
      const nw = w[k] + w[k + 1];
      v[k] = nv;
      w[k] = nw;
      i1[k] = i1[k + 1];
      v.splice(k + 1, 1);
      w.splice(k + 1, 1);
      i0.splice(k + 1, 1);
      i1.splice(k + 1, 1);
      while (k > 0 && v[k - 1] > v[k]) {
        const nv2 = (w[k - 1] * v[k - 1] + w[k] * v[k]) / (w[k - 1] + w[k]);
        const nw2 = w[k - 1] + w[k];
        v[k - 1] = nv2;
        w[k - 1] = nw2;
        i1[k - 1] = i1[k];
        v.splice(k, 1);
        w.splice(k, 1);
        i0.splice(k, 1);
        i1.splice(k, 1);
        k -= 1;
      }
    }
    const out = new Array(n);
    for (let bi = 0; bi < v.length; bi++) {
      for (let j = i0[bi]; j <= i1[bi]; j++) out[j] = v[bi];
    }
    return out;
  }

  function computeIconAngleOffsets(planetPositions, ascendantLongitude, angles) {
    const bodies = [];
    for (const [name, data] of Object.entries(planetPositions || {})) {
      bodies.push([name, longitudeToAngle(data.longitude, ascendantLongitude)]);
    }
    if (angles) {
      for (const [name, data] of Object.entries(angles)) {
        bodies.push([name, longitudeToAngle(data.longitude, ascendantLongitude)]);
      }
    }
    if (!bodies.length) return {};

    const names = bodies.map((b) => b[0]);
    const aDes = bodies.map((b) => b[1]);

    const r = Math.max(planetIconRadius, 1.0);
    // Per-icon packing: current uniform gap is the MAX (weight 1.0 = North Node).
    // Adjacent gap = 0.65 * (halfRad_a + halfRad_b) → equals old 1.3*halfRad when both=1.
    const iconPx = planetIconSize;
    const haloPx = 1.5;
    const safety = 1.05;
    const gapScale = 1.3;

    function halfRadFor(name) {
      const w = spacingWeight(name);
      const halfW = 0.5 * iconPx * w + haloPx;
      return Math.atan(halfW / r) * safety;
    }

    const order = argsort(aDes);
    const aSorted = order.map((i) => aDes[i]);
    const namesSorted = order.map((i) => names[i]);

    const gaps = [];
    for (let i = 0; i < aSorted.length; i++) {
      const next = i + 1 < aSorted.length ? aSorted[i + 1] : aSorted[0] + 2 * Math.PI;
      gaps.push(next - aSorted[i]);
    }
    let kBreak = 0;
    for (let i = 1; i < gaps.length; i++) {
      if (gaps[i] > gaps[kBreak]) kBreak = i;
    }

    const aRot = aSorted.slice(kBreak + 1).concat(aSorted.slice(0, kBreak + 1));
    const namesRot = namesSorted.slice(kBreak + 1).concat(namesSorted.slice(0, kBreak + 1));
    const aUnwrap = unwrap(aRot);

    const s = new Array(aUnwrap.length).fill(0);
    for (let i = 1; i < s.length; i++) {
      s[i] =
        (gapScale / 2) *
        (halfRadFor(namesRot[i - 1]) + halfRadFor(namesRot[i]));
    }
    const G = [];
    let acc = 0;
    for (let i = 0; i < s.length; i++) {
      acc += s[i];
      G.push(acc);
    }
    const u = aUnwrap.map((a, i) => a - G[i]);
    const uStar = pav(u);
    const y = uStar.map((val, i) => val + G[i]);

    const finalAngles = {};
    for (let i = 0; i < namesRot.length; i++) finalAngles[namesRot[i]] = y[i];

    const offsets = {};
    for (let i = 0; i < names.length; i++) {
      const n = names[i];
      const a0 = aDes[i];
      const af = finalAngles[n];
      // Python: (af - a0 + pi) % (2pi) - pi
      const dth = mod(af - a0 + Math.PI, 2 * Math.PI) - Math.PI;
      offsets[n] = dth;
    }
    return offsets;
  }

  function iconUrl(baseUrl, name, cacheBust) {
    const file = ICON_FILES[name];
    if (!file) return null;
    const base = String(baseUrl || "").replace(/\/?$/, "/");
    // Encode path segments (folders may contain spaces, e.g. "ASTRO SIGNS").
    const encoded = file
      .split("/")
      .map((seg) => encodeURIComponent(seg))
      .join("/");
    let url = base + encoded;
    if (cacheBust) {
      url +=
        (url.includes("?") ? "&" : "?") +
        "v=" +
        encodeURIComponent(String(cacheBust));
    }
    return url;
  }

  function placeIcon(svg, baseUrl, name, xMpl, yMpl, size, cacheBust) {
    const url = iconUrl(baseUrl, name, cacheBust);
    const p = toSvg(xMpl, yMpl);
    if (!url) {
      el(
        "circle",
        {
          cx: p.x,
          cy: p.y,
          r: 6,
          fill: "black",
        },
        svg
      );
      return;
    }
    el(
      "image",
      {
        href: url,
        x: p.x - size / 2,
        y: p.y - size / 2,
        width: size,
        height: size,
        preserveAspectRatio: "xMidYMid meet",
      },
      svg
    );
  }

  function lineMpl(svg, x1, y1, x2, y2, attrs) {
    const a = toSvg(x1, y1);
    const b = toSvg(x2, y2);
    el(
      "line",
      {
        x1: a.x,
        y1: a.y,
        x2: b.x,
        y2: b.y,
        ...attrs,
      },
      svg
    );
  }

  function circleMpl(svg, cx, cy, r, attrs) {
    const p = toSvg(cx, cy);
    el(
      "circle",
      {
        cx: p.x,
        cy: p.y,
        r,
        ...attrs,
      },
      svg
    );
  }

  function drawGrid(svg) {
    circleMpl(svg, center.x, center.y, positionRadius, {
      fill: "none",
      stroke: "black",
      "stroke-width": 2,
      "stroke-dasharray": "2 3",
      "stroke-opacity": 0.8,
    });
  }

  function drawAspects(svg, chartData, ascendantLongitude) {
    const aspects = chartData.aspects || [];
    const planetPositions = chartData.planet_positions || {};
    const angles = chartData.angles || {};

    function nodePos(body) {
      if (planetPositions[body]) {
        return planetGeo(planetPositions[body].longitude, ascendantLongitude).node;
      }
      if (angles[body]) {
        return planetGeo(angles[body].longitude, ascendantLongitude).node;
      }
      return null;
    }

    for (const asp of aspects) {
      const p1 = nodePos(asp.body1);
      const p2 = nodePos(asp.body2);
      if (!p1 || !p2) continue;
      const style = ASPECT_STYLES[asp.aspect] || { linewidth: 3.8, color: "#111827" };

      if (asp.aspect === "conjunction") {
        lineMpl(svg, p1.x, p1.y, p2.x, p2.y, {
          stroke: "#111827",
          "stroke-width": 5.0,
          "stroke-opacity": 0.15,
        });
      }
      lineMpl(svg, p1.x, p1.y, p2.x, p2.y, {
        stroke: style.color,
        "stroke-width": style.linewidth,
        "stroke-opacity": 0.95,
      });
    }
  }

  function drawRadialTicks(svg, chartData, ascendantLongitude) {
    for (const houseData of chartData.house_cusps || []) {
      const angle = longitudeToAngle(houseData.longitude, ascendantLongitude);
      let p1 = angleToPosition(angle, houseRingInner);
      let p2 = angleToPosition(angle, houseRingOuter);
      lineMpl(
        svg,
        p1.x + center.x,
        p1.y + center.y,
        p2.x + center.x,
        p2.y + center.y,
        { stroke: "black", "stroke-width": 2 }
      );

      p1 = angleToPosition(angle, houseRingOuter);
      p2 = angleToPosition(angle, positionRadius);
      lineMpl(
        svg,
        p1.x + center.x,
        p1.y + center.y,
        p2.x + center.x,
        p2.y + center.y,
        {
          stroke: "black",
          "stroke-width": 1.5,
          "stroke-dasharray": "6 4",
          "stroke-opacity": 0.8,
        }
      );
    }
  }

  function drawZodiacRing(
    svg,
    chartData,
    ascendantLongitude,
    signIconsBaseUrl,
    cacheBust
  ) {
    circleMpl(svg, center.x, center.y, signRingOuter, {
      fill: "none",
      stroke: "black",
      "stroke-width": 2,
    });
    circleMpl(svg, center.x, center.y, signRingInner, {
      fill: "none",
      stroke: "black",
      "stroke-width": 2,
    });

    for (let i = 0; i < 12; i++) {
      const angle = longitudeToAngle(i * 30, ascendantLongitude);
      const p1 = angleToPosition(angle, signRingInner);
      const p2 = angleToPosition(angle, signRingOuter);
      lineMpl(
        svg,
        p1.x + center.x,
        p1.y + center.y,
        p2.x + center.x,
        p2.y + center.y,
        { stroke: "black", "stroke-width": 2 }
      );
    }

    for (let i = 0; i < SIGNS.length; i++) {
      const angle = longitudeToAngle(i * 30 + 15, ascendantLongitude);
      const p = angleToPosition(angle, signIconRadius);
      placeIcon(
        svg,
        signIconsBaseUrl,
        SIGNS[i],
        p.x + center.x,
        p.y + center.y,
        signIconSize,
        cacheBust
      );
    }
  }

  function drawHouseRing(
    svg,
    chartData,
    ascendantLongitude,
    iconsBaseUrl,
    cacheBust
  ) {
    circleMpl(svg, center.x, center.y, houseRingOuter, {
      fill: "none",
      stroke: "black",
      "stroke-width": 2,
    });
    circleMpl(svg, center.x, center.y, houseRingInner, {
      fill: "none",
      stroke: "black",
      "stroke-width": 2,
    });

    const houseCusps = chartData.house_cusps || [];
    for (let i = 0; i < houseCusps.length; i++) {
      const houseData = houseCusps[i];
      const houseNumber = String(houseData.house_number);
      let currentCusp = houseData.longitude;
      let nextCusp = houseCusps[(i + 1) % 12].longitude;
      if (nextCusp < currentCusp) nextCusp += 360;
      let centerAngle = (currentCusp + nextCusp) / 2;
      if (centerAngle >= 360) centerAngle -= 360;

      const angle = longitudeToAngle(centerAngle, ascendantLongitude);
      const p = angleToPosition(angle, houseIconRadius);
      placeIcon(
        svg,
        iconsBaseUrl,
        houseNumber,
        p.x + center.x,
        p.y + center.y,
        houseIconSize,
        cacheBust
      );
    }
  }

  function drawBodies(
    svg,
    chartData,
    ascendantLongitude,
    iconsBaseUrl,
    cacheBust
  ) {
    const planetPositions = chartData.planet_positions || {};
    const angles = chartData.angles || {};
    const offsets = computeIconAngleOffsets(
      planetPositions,
      ascendantLongitude,
      angles
    );

    function drawOne(name, longitude) {
      const geo = planetGeo(longitude, ascendantLongitude);
      lineMpl(svg, geo.on.x, geo.on.y, geo.tick.x, geo.tick.y, {
        stroke: "black",
        "stroke-width": 5,
        "stroke-linecap": "round",
      });

      const dth = offsets[name] || 0;
      const iconAngle = geo.angle + dth;
      const ip = angleToPosition(iconAngle, planetIconRadius);
      const size =
        name === "Ascendant" || name === "MC" ? angleIconSize : planetIconSize;
      placeIcon(
        svg,
        iconsBaseUrl,
        name,
        ip.x + center.x,
        ip.y + center.y,
        size,
        cacheBust
      );

      circleMpl(svg, geo.node.x, geo.node.y, nodeRadiusPx, {
        fill: "none",
        stroke: "black",
        "stroke-width": 2.2,
      });
    }

    for (const [planet, data] of Object.entries(planetPositions)) {
      drawOne(planet, data.longitude);
    }
    for (const [angleName, data] of Object.entries(angles)) {
      drawOne(angleName, data.longitude);
    }
  }

  /**
   * @param {HTMLElement|string} container
   * @param {object} chartData
   * @param {{ iconsBaseUrl?: string, signIconsBaseUrl?: string, iconsCacheBust?: string }} [options]
   *   iconsBaseUrl — CDN or local root of normalized icons
   *     (default: https://images.plumastro.com/birthcharticons)
   *   signIconsBaseUrl — optional override; defaults to same as iconsBaseUrl
   *   iconsCacheBust — optional query param to bust browser icon cache
   * @returns {SVGSVGElement}
   */
  function renderBirthChartWheel(container, chartData, options) {
    const root =
      typeof container === "string"
        ? document.querySelector(container)
        : container;
    if (!root) throw new Error("renderBirthChartWheel: container not found");
    const opts = options || {};
    // Default: public R2 / CDN mirror of icons/normalized/
    const iconsBaseUrl =
      opts.iconsBaseUrl || "https://images.plumastro.com/birthcharticons";
    const signIconsBaseUrl = opts.signIconsBaseUrl || iconsBaseUrl;
    const iconsCacheBust = opts.iconsCacheBust || "";

    root.innerHTML = "";
    const svg = el("svg", {
      viewBox: `0 0 ${canvasSize} ${canvasSize}`,
      width: "100%",
      height: "100%",
      xmlns: NS,
      style: "display:block;background:transparent;",
    });
    root.appendChild(svg);

    const pp = chartData.planet_positions || {};
    const ascendantLongitude =
      pp.Ascendant != null ? pp.Ascendant.longitude : null;

    // Draw order matches renderer.render_birth_chart
    drawGrid(svg);
    drawAspects(svg, chartData, ascendantLongitude);
    drawRadialTicks(svg, chartData, ascendantLongitude);
    drawHouseRing(
      svg,
      chartData,
      ascendantLongitude,
      iconsBaseUrl,
      iconsCacheBust
    );
    drawZodiacRing(
      svg,
      chartData,
      ascendantLongitude,
      signIconsBaseUrl,
      iconsCacheBust
    );
    drawBodies(
      svg,
      chartData,
      ascendantLongitude,
      iconsBaseUrl,
      iconsCacheBust
    );

    return svg;
  }

  // Expose for browser + optional module usage
  global.renderBirthChartWheel = renderBirthChartWheel;
  if (typeof module !== "undefined" && module.exports) {
    module.exports = { renderBirthChartWheel };
  }
})(typeof window !== "undefined" ? window : globalThis);
